import { useCalendarAppointments } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarAppointmentsContext";
import { useSessionTypes } from "contexts/SessionTypesContext";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { useInvoicingModalContext } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/invoicing-modal-context";
import formatTimeRange from "components/timeslot-popover/utils/formatTimeRange";
import moment, { MomentInput } from "moment-timezone";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cancelInvoicingSession from "services/invoicing/cancelInvoicingSession";
import editInvoice from "services/invoicing/editInvoice";
import {
  closeSidesheet,
  selectFocusedSessionNextStatus,
  setFocusedSessionNextStatus,
} from "store/calendarSidebarSlice";
import formatSessionDuration from "utils/formatSessionDuration";
import { getCoupleDisplayShortName } from "utils/getCoupleDisplayName";
import { getAppointmentPatientName } from "../../../../../calendar-view/components/scrollable-calendar-view/components/appointment-timeslot/utils/getAppointmentPatientName";

type PatientType = "minor" | "adult" | "couple";

const useMainSessionDetailsSection = ({
  appointment,
  start,
  end,
  diffHrs,
  setError,
}: {
  appointment: AppointmentWithSeriesInfo & {
    invoice?: InvoiceData;
  };
  start: MomentInput;
  end: MomentInput;
  diffHrs: number;
  setError: React.Dispatch<any>;
}) => {
  const dispatch = useDispatch();

  const { sessionTypes } = useSessionTypes();
  const { currentCalendarTimezone } = useCalendarTime();
  const { openInvoicingModal } = useInvoicingModalContext();
  const { forceRefreshAppointments } = useCalendarAppointments();

  const nextStatus = useSelector(selectFocusedSessionNextStatus);

  const patientName = useMemo(
    () =>
      appointment.couple && appointment.couple.length > 0
        ? getCoupleDisplayShortName(appointment.couple)
        : getAppointmentPatientName(appointment, true),
    [appointment],
  );

  const patientType: PatientType = useMemo(() => {
    const isMinor = !!appointment?.guardians?.length;
    if (isMinor) {
      return "minor";
    }

    const isCouple = !!appointment?.couple?.length;
    if (isCouple) {
      return "couple";
    }

    return "adult";
  }, [appointment]);

  const sessionType = useMemo(
    () =>
      sessionTypes.find(
        (sessionType) =>
          sessionType.label === appointment.appointment_type_label,
      ),
    [appointment, sessionTypes],
  );

  const dateString = useMemo(
    () => moment(start).tz(currentCalendarTimezone).format("dddd, MMMM D "),
    [start, currentCalendarTimezone],
  );

  const timeString = useMemo(
    () =>
      `${formatTimeRange(
        start,
        end,
        currentCalendarTimezone,
      )}  (${formatSessionDuration(diffHrs * 60)})`,
    [start, end, currentCalendarTimezone, diffHrs],
  );

  const priceString = useMemo(
    () => `$${appointment.pricing.list_price.toFixed(2)}`,
    [appointment.pricing.list_price],
  );

  const alterInvoicingSessionStatus = useCallback(
    async (status) => {
      if (!appointment.invoice?.invoice_no) return;
      try {
        const response = await editInvoice(appointment.invoice.invoice_no, {
          appointment_status: status,
        });
        const { appointment_status } = response;
        dispatch(setFocusedSessionNextStatus(appointment_status));
        forceRefreshAppointments();
      } catch (error) {
        setError(error);
      }
    },
    [
      dispatch,
      appointment.invoice?.invoice_no,
      forceRefreshAppointments,
      setError,
    ],
  );

  const cancelInvoicingAppointment = useCallback(
    async (cancelStatus) => {
      if (!!appointment.invoice?.invoice_no) {
        return;
      }
      try {
        await cancelInvoicingSession(
          appointment.appointment_id.toString(),
          cancelStatus,
        );
        dispatch(closeSidesheet());
      } catch (error) {
        setError(error);
      }
    },
    [
      dispatch,
      appointment.invoice?.invoice_no,
      appointment.appointment_id,
      setError,
    ],
  );

  const onInvoicingToggleStatusChange = useCallback(
    (status: string) => {
      if (status === "placeholder") {
        openInvoicingModal({
          appointment: appointment,
        });
        return;
      }

      if (appointment.invoice) {
        alterInvoicingSessionStatus(status);
        return;
      }

      cancelInvoicingAppointment(status);
    },
    [
      appointment,
      openInvoicingModal,
      alterInvoicingSessionStatus,
      cancelInvoicingAppointment,
    ],
  );

  return {
    patientType,
    patientName,
    sessionType,
    dateString,
    timeString,
    priceString,
    nextStatus,
    onInvoicingToggleStatusChange,
  };
};

export default useMainSessionDetailsSection;
